import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { getAdminCommunicationsAsync } from '../../ajax/requests';
import { DateField } from '../../components/Forms/fields';
import Typography from '../../components/Typography';
import { loadEntities } from '../../store/entities/actions';
import { useEntities } from '../../store/entities/hooks';
import { isLoadingEntities } from '../../store/entities/selectors';
import { CommunicationEntity } from '../../store/entities/state';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import './AdministratorDashboard.scss';
import { getNowDateString } from '../../shared/utils';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import PersonIcon from '@material-ui/icons/Person';
import { CommunicationStatus } from '../../models';

const CommunicationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacer-M);
  padding: var(--spacer-M);
`;

const CommunicationCard = styled.div<{ children?: React.ReactNode }>`
  background: white;
  border: var(--border-thin) solid var(--appLigthGrey);
  border-radius: var(--spacer-XS);
  padding: var(--spacer-M);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 var(--spacer-XXS) var(--spacer-S) rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div<{ children?: React.ReactNode }>`
  display: flex;
  align-items: center;
  gap: var(--spacer-S);
  margin-bottom: var(--spacer-S);
  padding-bottom: var(--spacer-S);
  border-bottom: var(--border-thin) solid var(--bg-light);
`;

const IconWrapper = styled.div<{ type: 'email' | 'sms'; children?: React.ReactNode }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${props => props.type === 'email'
    ? 'var(--info-color)'
    : 'var(--success-color)'};
  color: white;

  svg {
    font-size: 1.25rem;
  }
`;

const CardMeta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacer-XXS);
`;

const RecipientName = styled.div`
  font-weight: 600;
  color: var(--text-body);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: var(--spacer-XS);
`;

const Timestamp = styled.div`
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const CountBadge = styled.span`
  background: var(--appLigthGrey);
  color: var(--text-body);
  padding: var(--spacer-XXS) var(--spacer-XS);
  border-radius: var(--spacer-XXS);
  font-size: 0.75rem;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ status: CommunicationStatus; children?: React.ReactNode }>`
  padding: var(--spacer-XXS) var(--spacer-XS);
  border-radius: var(--spacer-XXS);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case CommunicationStatus.Pending: return 'var(--warning-bg)';
      case CommunicationStatus.Failed: return 'var(--danger-color)';
      default: return 'var(--success-bg)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case CommunicationStatus.Pending: return 'var(--text-body)';
      case CommunicationStatus.Failed: return 'white';
      default: return 'var(--text-body)';
    }
  }};
  border: var(--border-thin) solid ${props => {
    switch (props.status) {
      case CommunicationStatus.Pending: return 'var(--warning-border)';
      case CommunicationStatus.Failed: return 'var(--danger-color)';
      default: return 'var(--success-border)';
    }
  }};
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--spacer-XS) var(--spacer-S);
  margin-bottom: var(--spacer-S);
  font-size: 0.85rem;
`;

const Label = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const Value = styled.span`
  color: var(--text-body);
`;

const MessageContent = styled.div`
  background: var(--bg-light);
  padding: var(--spacer-S);
  border-radius: var(--spacer-XXS);
  border-left: var(--border-thick) solid var(--info-color);
  color: var(--text-body);
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Subject = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacer-XS);
  color: var(--text-body);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacer-L);
  color: var(--text-muted);
  font-size: 0.95rem;
`;

const DateFieldWrapper = styled.div`
  margin-bottom: var(--spacer-M);
`;

export const CommunicationsTab = () => {
  const t = useTranslations()
  const dispatch = useDispatch()
  const communications = useEntities<CommunicationEntity>('communications')
  const loading = useSelector(isLoadingEntities('communications'))

  const [commDate, setCommDate] = useState<string>(getNowDateString())

  const communicationsSelectedDate = React.useMemo(() => communications.filter(c => c.timestamp.startsWith(commDate)), [commDate, communications])

  useEffect(() => {
    dispatch(loadEntities('communications', () => getAdminCommunicationsAsync(commDate, commDate), 'AppendResult', communicationsSelectedDate.map(x => x.id)))
  }, [commDate])


  const formatTimestamp = (timestamp: string) => {
    return timestamp.substring(0, 16).replace('T', ' ');
  };

  const cleanText = (text: string) => {
    return text
      .replace(/(<style.*<\/style>)|(<([^>]+)>)|(\/\*.*\*\/)/gi, ' ')
      .replace(/[ \t]{2,}/gi, ' ')
      .trim();
  };

  const isSms = (comm: CommunicationEntity) => {
    return comm.to?.match(/^\+?[0-9\s]+$/);
  };

  const getStatusLabel = (status: CommunicationStatus) => {
    switch (status) {
      case CommunicationStatus.Pending: return t(TK.pending);
      case CommunicationStatus.Failed: return t(TK.failed);
      default: return t(TK.sent);
    }
  };

  return (
    <CommunicationsContainer>
      <DateFieldWrapper>
        <DateField
          label={t(TK.date)}
          type='date'
          value={commDate}
          onChange={setCommDate}
        />
      </DateFieldWrapper>

      <Typography style={{visibility: loading ? 'unset':'hidden'}}>
        {t(TK.loading)}...
      </Typography>

      {!loading && communicationsSelectedDate.length === 0 && (
        <EmptyState>
          {t(TK.noDataAvailable)}
        </EmptyState>
      )}

      {communicationsSelectedDate.map(comm => (
        <CommunicationCard key={comm.id}>
          <CardHeader>
            <IconWrapper type={isSms(comm) ? 'sms' : 'email'}>
              {isSms(comm) ? <SmsIcon /> : <EmailIcon />}
            </IconWrapper>
            <CardMeta>
              <RecipientName>
                <PersonIcon style={{ fontSize: '1rem', color: 'var(--text-muted)' }} />
                {comm.clientName || comm.username || comm.professionalName}
                {comm.count > 1 && (
                  <CountBadge>{comm.count}</CountBadge>
                )}
              </RecipientName>
              <Timestamp>
                {formatTimestamp(comm.timestamp)}
                {' '}
                <StatusBadge status={comm.status ?? CommunicationStatus.Sent}>
                  {getStatusLabel(comm.status ?? CommunicationStatus.Sent)}
                </StatusBadge>
              </Timestamp>
            </CardMeta>
          </CardHeader>

          <ContactInfo>
            <Label>{t(TK.from)}:</Label>
            <Value>{comm.from}</Value>
            <Label>{t(TK.to)}:</Label>
            <Value>{comm.to}</Value>
          </ContactInfo>

          <MessageContent>
            {comm.subject && (
              <Subject>{comm.subject}</Subject>
            )}
            {cleanText(comm.text)}
          </MessageContent>
        </CommunicationCard>
      ))}
    </CommunicationsContainer>
  );
} 